/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { Label, Stack, StackItem, Text } from "@fluentui/react";

import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import {
  awardCategoryStatuses,
  inactiveImpactList,
  unaffectedList,
  updatePanelErrorMessage,
} from "Constants/AwardCategoriesConstants";
import {
  getSelectedAwardCategory,
  getUpdateACErrorMessage,
  selectAwardCategory,
  setIsAwardCategoryUpdated,
  updateACErrorMessage,
} from "Dataflows/AwardCategories/AwardCategoriesSlice";
import { useUpdateAwardCategoryMutation } from "Services/AwardCategoriesApi";

import IAwardCategoriesUpdatePanelProps from "./IAwardCategoriesUpdatePanelProps";

export const AwardCategoriesUpdatePanel: React.FunctionComponent<
  IAwardCategoriesUpdatePanelProps
> = (props: IAwardCategoriesUpdatePanelProps) => {
  const { isOpen, setIsOpen } = props;
  const selectedAwardCategory = useSelector(getSelectedAwardCategory);
  const errorMessage = useSelector(getUpdateACErrorMessage);
  const [updateAwardCategory, { isLoading: isSaving, error: mutationError }] =
    useUpdateAwardCategoryMutation(); // Destructure mutation
  const dispatch = useDispatch();

  // Default values for status dropdown
  const defaultValues = {
    Status: selectedAwardCategory?.isActive ? "Active" : "Inactive", // Use current status or default to "Active"
  };

  // Effect to sync error state between mutation and Redux
  useEffect(() => {
    if (mutationError) {
      // If mutation fails, set the error message
      dispatch(updateACErrorMessage(updatePanelErrorMessage));
    } else {
      // Clear error message if no errors
      dispatch(updateACErrorMessage(""));
    }
  }, [mutationError]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const dismissPanel = (): void => {
    setIsOpen(false);
    reset(); // Reset form when the panel is closed
  };

  // Save function to handle form submission
  const onSave = async (data: { Status: string }) => {
    if (data.Status === "Active") {
      dispatch(updateACErrorMessage("Status update is required"));
    } else {
      try {
        const isActive = data.Status === "Active" ? true : false;
        await updateAwardCategory({
          ...selectedAwardCategory,
          IsActive: isActive,
        }).unwrap();
        if (!mutationError) {
          dismissPanel();
          dispatch(setIsAwardCategoryUpdated(true)); // Set flag to indicate award category update
          dispatch(
            selectAwardCategory({
              ...selectedAwardCategory,
              isActive: false,
            })
          ); // update status of selected award category
          dispatch(updateACErrorMessage("")); // Clear error message
        }
      } catch (error) {
        dispatch(updateACErrorMessage(updatePanelErrorMessage));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <SidePanel
        isOpen={isOpen}
        title={selectedAwardCategory.technologyFocusAreaGroupName}
        saveButtonLabel="Save"
        error={errorMessage}
        disableActions={isSaving || !selectedAwardCategory?.isActive} // Disable actions if saving
        onDismiss={dismissPanel}
        onSave={handleSubmit(onSave)} // Trigger form submission on save
      >
        <StackItem align="start">
          <Text>Making an Award Category (AC) as Inactive will:</Text>
          <ul>
            {inactiveImpactList.map((item, index) => (
              <li key={`inactive-impact-${index}`}>
                <Text>{item}</Text>
              </li>
            ))}
          </ul>
          <Text>It will not affect:</Text>
          <ul>
            {unaffectedList.map((item, index) => (
              <li key={`unaffected-impact-${index}`}>
                <Text>{item}</Text>
              </li>
            ))}
          </ul>
        </StackItem>
        <StackItem>
          <Stack tokens={{ childrenGap: "1rem" }} horizontal={true} horizontalAlign="space-evenly">
            <StackItem align="center" grow={100}>
              <Label htmlFor="Status">Status</Label>
            </StackItem>
            <StackItem align="start" grow={100}>
              <ComboBoxController
                errors={errors}
                control={control}
                inputName="Status"
                options={awardCategoryStatuses}
                isDisabled={isSaving || !selectedAwardCategory?.isActive} // Disable ComboBox if saving
              />
            </StackItem>
          </Stack>
        </StackItem>
      </SidePanel>
    </form>
  );
};
